import { configureStore } from '@reduxjs/toolkit';
import authSlice from './slices/auth/authSlice';
import videoSlice from './slices/video/videoSlice';
import videoManagementSlice from './slices/videoManagement/videoManagementSlice';
import premiumPackagesSlice from './slices/premiumPackages/premiumPackagesSlice';
import exclusiveContentSlice from './slices/exclusiveContent/exclusiveContentSlice';
import walletSlice from './slices/wallet/walletSlice';
import paymentSlice from './slices/payment/paymentSlice';
import uploadGlobalOverlayProgressSlice from './slices/uploadGlobalOverlayProgress/uploadGlobalOverlayProgressSlice';
import appOverlaySlice from './slices/appOverlay/appOverlaySlice';

/**
 * Redux Store - App-level global state ONLY
 * 
 * ✅ Use Redux for:
 * - Authentication state (needed everywhere)
 * - Video state (decryption keys, prevents re-initialization)
 * - Video management (playback control across screens)
 * - Premium packages (user subscriptions needed synchronously across features)
 * - Exclusive-content subscriptions (per-creator subscription state needed
 *   synchronously by content filters and unlock UI)
 * - App-level overlays (budget-exceeded / account-suspended blocking overlays
 *   and the updated-legal-document modal queue, raised from unrelated features
 *   and even from the static Navigator)
 *
 * ❌ Don't use Redux for:
 * - Feature-specific data (feed, user details, etc.)
 * - Use feature hooks instead: src/presentation/feature/[feature]/[screen]/hooks/
 */
export const store = configureStore({
  reducer: {
    auth: authSlice,
    video: videoSlice,
    videoManagement: videoManagementSlice,
    premiumPackages: premiumPackagesSlice,
    exclusiveContent: exclusiveContentSlice,
    wallet: walletSlice,
    payment: paymentSlice,
    uploadProgress: uploadGlobalOverlayProgressSlice,
    appOverlay: appOverlaySlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['auth/setUser', 'video/initialize/fulfilled'],
        ignoredPaths: ['auth.user', 'video.videos'],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;